"use client";

import { useCallback, useRef } from "react";

import type { SvgAnimationHandle } from "@perceptron/ui/components/svg-animation/svg-animation";

/**
 * How a hovered icon returns to its first frame. `finish`: a run plays to its end and holds
 * there while hovered, and snaps back once the pointer has left (after the run completes).
 * `reverse`: the run plays forward while hovered, holding its end frame, and plays back to the
 * first frame from wherever it is once the pointer leaves. Either way the icon rests at its
 * initial state whenever it is not hovered.
 */
export type HoverPlayback = "finish" | "reverse";

const animationsOf = (svg: SVGSVGElement | null) => svg?.getAnimations?.({ subtree: true }) ?? [];
const isPlaying = (animation: Animation) => animation.playState === "running" || animation.pending;

/**
 * Hover playback for an animation-capable `Icon`: pass `animationRef` and `svgRef` (as the
 * icon's `animationRef` and `ref`) and the pointer handlers to the hovered control.
 */
export function useIconHoverPlayback(mode: HoverPlayback) {
  const animationRef = useRef<SvgAnimationHandle>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const hovered = useRef(false);
  // Identifies the latest leave, so an earlier leave's completion cannot reset a newer hover.
  const leaveToken = useRef(0);
  // The direction the reverse-mode icon was last sent in; mirrors the handle, which only toggles.
  const direction = useRef<"forward" | "reverse">("forward");

  // Seek to the resting frame without playing, so the icon rests exactly where it started.
  const resetToStart = useCallback(() => {
    for (const animation of animationsOf(svgRef.current)) {
      animation.pause();
      animation.currentTime = 0;
    }
  }, []);

  const playBack = useCallback(() => {
    if (direction.current === "reverse") return;
    animationRef.current?.reverse();
    direction.current = "reverse";
  }, []);

  const onPointerEnter = useCallback(() => {
    hovered.current = true;
    if (mode === "reverse") {
      // Resumes forward from the current position, including mid-reverse.
      animationRef.current?.play();
      direction.current = "forward";
      return;
    }
    // One run per hover; a run still finishing from the previous hover is left to complete.
    if (animationsOf(svgRef.current).some(isPlaying)) return;
    animationRef.current?.restart("forward");
  }, [mode]);

  const onPointerLeave = useCallback(() => {
    hovered.current = false;
    if (mode === "reverse") {
      playBack();
      return;
    }
    const playing = animationsOf(svgRef.current).filter(isPlaying);
    if (playing.length === 0) {
      resetToStart();
      return;
    }
    // Let the run complete, then reset unless the pointer came back in the meantime.
    const token = ++leaveToken.current;
    let remaining = playing.length;
    const settled = () => {
      remaining -= 1;
      if (remaining === 0 && leaveToken.current === token && !hovered.current) resetToStart();
    };
    for (const animation of playing) {
      animation.addEventListener("finish", settled, { once: true });
      animation.addEventListener("cancel", settled, { once: true });
    }
  }, [mode, playBack, resetToStart]);

  return { animationRef, svgRef, onPointerEnter, onPointerLeave };
}
